import type { ApiErrorCode, ApiResponse, Meta } from '../types/api'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

const CHAVE_TOKEN = 'polo.sessao'

/**
 * O token vive no localStorage, e nao num cookie: o app roda em WebView com
 * origin proprio (capacitor://localhost) e cookie entre origens diferentes
 * nao sobrevive.
 */
export const sessao = {
  ler(): string | null {
    try {
      return localStorage.getItem(CHAVE_TOKEN)
    } catch {
      return null
    }
  },
  gravar(token: string): void {
    try {
      localStorage.setItem(CHAVE_TOKEN, token)
    } catch {
      // Modo privado: a sessao dura só enquanto a aba estiver aberta.
    }
  },
  limpar(): void {
    try {
      localStorage.removeItem(CHAVE_TOKEN)
    } catch {
      // Ignorado de propósito.
    }
  },
}

// Campos declarados explicitamente: o tsconfig usa erasableSyntaxOnly,
// que proibe propriedades declaradas no construtor.
export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number

  constructor(code: ApiErrorCode, message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

export interface Paginado<T> {
  itens: T[]
  meta: Meta
}

function montarUrl(caminho: string, params?: Record<string, unknown>): string {
  const url = `${BASE}${caminho}`
  if (!params) return url
  const q = new URLSearchParams()
  for (const [chave, valor] of Object.entries(params)) {
    if (valor === undefined || valor === null || valor === '' || valor === false) continue
    q.set(chave, valor === true ? '1' : String(valor))
  }
  const s = q.toString()
  return s ? `${url}?${s}` : url
}

async function requisitar<T>(
  caminho: string,
  params?: Record<string, unknown>,
  init?: RequestInit,
): Promise<ApiResponse<T>> {
  const token = sessao.ler()
  let res: Response
  try {
    res = await fetch(montarUrl(caminho, params), {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    })
  } catch {
    // Rede fora do ar: o fetch rejeita antes de haver status.
    throw new ApiError('INTERNAL', 'Sem conexão com o servidor.', 0)
  }

  let corpo: ApiResponse<T>
  try {
    corpo = (await res.json()) as ApiResponse<T>
  } catch {
    throw new ApiError('INTERNAL', 'Resposta inválida do servidor.', res.status)
  }

  if (!corpo.ok) {
    // 401 significa token invalido ou expirado: derruba a sessao local para o
    // app nao ficar preso tentando com uma credencial morta.
    if (res.status === 401) sessao.limpar()
    throw new ApiError(corpo.error.code, corpo.error.message, res.status)
  }
  return corpo
}

/** Endpoints sem paginacao. */
export async function fetchJson<T>(
  caminho: string,
  params?: Record<string, unknown>,
): Promise<T> {
  const r = await requisitar<T>(caminho, params)
  return (r as { ok: true; data: T }).data
}

/** Endpoints paginados: devolve itens e meta juntos. */
export async function fetchPaginado<T>(
  caminho: string,
  params?: Record<string, unknown>,
): Promise<Paginado<T>> {
  const r = (await requisitar<T[]>(caminho, params)) as { ok: true; data: T[]; meta?: Meta }
  return {
    itens: r.data,
    meta: r.meta ?? {
      page: 1,
      per_page: r.data.length,
      total: r.data.length,
      total_pages: 1,
      has_more: false,
    },
  }
}

export async function postJson<T>(caminho: string, corpo: unknown): Promise<T> {
  const r = await requisitar<T>(caminho, undefined, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  })
  return (r as { ok: true; data: T }).data
}

export async function postFormData<T>(caminho: string, form: FormData): Promise<T> {
  const r = await requisitar<T>(caminho, undefined, { method: 'POST', body: form })
  return (r as { ok: true; data: T }).data
}

export function mensagemDoErro(erro: unknown): string {
  if (!(erro instanceof ApiError)) return 'Algo deu errado. Tente novamente.'
  switch (erro.code) {
    case 'UPSTREAM_DB':
      return 'Estamos com instabilidade no servidor. Tente em instantes.'
    case 'NOT_FOUND':
      return 'Não encontramos o que você procura.'
    case 'RATE_LIMITED':
      return 'Você fez muitas tentativas. Aguarde um pouco.'
    case 'BAD_REQUEST':
      return erro.message
    default:
      return erro.status === 0
        ? 'Sem conexão. Verifique sua internet.'
        : 'Algo deu errado. Tente novamente.'
  }
}
